import { defineStore, acceptHMRUpdate } from 'pinia';
import keyBy from 'lodash/keyBy';
import values from 'lodash/values';
import get from 'lodash/get';
import { useAppsStore } from '@/stores/apps.store';
import { useGuidesStore } from '@/stores/guides.store';
import { ExperimentsService } from '@/guides/services/experiments-service';
import { createVariantUpdate, validateVariantUpdate } from '@/utils/experiment.utils';
import type {
    ExperimentState,
    ExperimentVariant,
    Guide as ExperimentGuide,
} from '@/utils/experiment.utils';
import { getDefaultSegment } from '@utils/segments';
import { buildAudiencePipeline } from '@/guides/helpers/common.util';
import { experimentActivatedTrackEvent } from '@/guides/track-events/experiments/experiment-activated';

interface AudienceUiHint {
    filters?: Array<{
        segmentId?: string;
        [key: string]: unknown;
    }>;
    [key: string]: unknown;
}

export interface ExperimentRecord {
    id: string;
    name?: string;
    appId?: string | number;
    state?: ExperimentState;
    variants?: ExperimentVariant[];
    audience?: unknown;
    audienceUiHint?: AudienceUiHint;
    [key: string]: unknown;
}

interface ExperimentsState {
    experiments: Record<string, ExperimentRecord>;
    experiment: ExperimentRecord | null;
    variantResults: unknown;
    loading: boolean;
    saving: boolean;
    error: unknown;
}

interface ApiResponse<T> {
    data: T;
}

interface ExperimentFetchOptions {
    reFetch?: boolean;
}

interface ExperimentChanges {
    [key: string]: unknown;
}

interface ExperimentActionOptions {
    variantUpdates?: boolean;
    statusUpdate?: boolean;
}

interface UpdateExperimentPayload {
    id: string;
    experiment: ExperimentChanges;
    options?: ExperimentActionOptions;
}

interface GuideReference {
    id: string;
    [key: string]: unknown;
}

interface UpdateVariantPayload {
    id: string;
    variant: 'A' | 'B';
    guide: GuideReference;
    segment?: unknown;
}

interface CreateAudienceUpdatePayload {
    appId: string | number;
    experiment: ExperimentRecord;
    segment?: unknown;
}

interface AudienceUpdate {
    audience: unknown;
    audienceUiHint: unknown;
}

let loadAllPromise: Promise<ApiResponse<ExperimentRecord[]>> | null = null;

export const useExperimentsStore = defineStore('experiments', {
    state: (): ExperimentsState => ({
        experiments: {},
        experiment: null,
        variantResults: null,
        loading: false,
        saving: false,
        error: null,
    }),
    getters: {
        isLoading: (state) => state.loading,
        isSaving: (state) => state.saving,
        hasError: (state) => !!state.error,
        getError: (state) => state.error,
        getAllExperiments: (state) => values(state.experiments),
        getCurrentExperiment: (state) => state.experiment,
        getExperimentById: (state) => (id: string) => state.experiments[id],
    },
    actions: {
        setExperiments (experimentsArray: ExperimentRecord[]) {
            this.experiments = keyBy(experimentsArray, 'id');
        },
        setExperiment (experiment: ExperimentRecord | null) {
            this.experiment = experiment;
        },
        addExperiment (experiment: ExperimentRecord) {
            this.experiments[experiment.id] = experiment;
        },
        updateExperimentInState (updatedExperiment: ExperimentRecord) {
            if (this.experiments[updatedExperiment.id]) {
                this.experiments[updatedExperiment.id] = updatedExperiment;
            }

            if (this.experiment && this.experiment.id === updatedExperiment.id) {
                this.experiment = updatedExperiment;
            }
        },
        removeExperiment (experimentId: string) {
            delete this.experiments[experimentId];
            if (this.experiment && this.experiment.id === experimentId) {
                this.experiment = null;
            }
        },

        async fetchAllExperiments ({ reFetch = false }: ExperimentFetchOptions = {}) {
            if (loadAllPromise && !reFetch) {
                try {
                    const response = await loadAllPromise;

                    return response.data;
                } catch (error) {
                    throw error;
                }
            }

            this.loading = true;
            this.error = null;

            const currentFetchPromise = ExperimentsService.getExperiments() as unknown as Promise<
                ApiResponse<ExperimentRecord[]>
            >;
            loadAllPromise = currentFetchPromise;

            try {
                const response = await currentFetchPromise;

                if (loadAllPromise === currentFetchPromise) {
                    this.setExperiments(response.data);
                }

                return response.data;
            } catch (error) {
                if (loadAllPromise === currentFetchPromise) {
                    loadAllPromise = null;
                }
                this.error = error;
                throw error;
            } finally {
                if (loadAllPromise === currentFetchPromise) {
                    this.loading = false;
                }
            }
        },

        async fetchExperiment ({ id, reFetch = false }: { id: string } & ExperimentFetchOptions) {
            const loadedExperiment = this.getExperimentById(id);
            if (!reFetch && loadedExperiment) return Promise.resolve(loadedExperiment);

            this.loading = true;
            this.error = null;

            try {
                const response = await ExperimentsService.getExperiment(id) as unknown as ApiResponse<ExperimentRecord>;
                this.setExperiment(response.data);

                return response.data;
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.loading = false;
            }
        },

        async createExperiment ({ experiment }: { experiment: ExperimentChanges }) {
            this.saving = true;
            this.error = null;

            try {
                const response = await ExperimentsService.createExperiment(experiment) as unknown as ApiResponse<
                    ExperimentRecord
                >;
                this.addExperiment(response.data);
                this.setExperiment(response.data);

                return response.data;
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.saving = false;
            }
        },

        async updateExperiment ({ id, experiment, options = {} }: UpdateExperimentPayload) {
            this.saving = true;
            this.error = null;

            const { variantUpdates = false, statusUpdate = false } = options;
            const previousVariantIds = this.getExperimentById(id)?.variants?.map(({ guideId }) => guideId) || [];

            try {
                const { data: updatedExperiment } = await ExperimentsService.updateExperiment(
                    id,
                    experiment,
                ) as unknown as ApiResponse<ExperimentRecord>;
                this.updateExperimentInState(updatedExperiment);

                if (variantUpdates || statusUpdate) {
                    const newVariantIds = updatedExperiment.variants?.map(({ guideId }) => guideId) || [];
                    const guideIdsToRefresh = [...new Set([...previousVariantIds, ...newVariantIds])].filter(Boolean);

                    if (guideIdsToRefresh.length > 0) {
                        const guideReloadPromises = guideIdsToRefresh.map((guideId: string) =>
                            useGuidesStore().loadOne({ id: guideId, reFetch: true }),
                        );
                        await Promise.all(guideReloadPromises);
                    }
                }

                if (statusUpdate && updatedExperiment.state === 'active') {
                    experimentActivatedTrackEvent(updatedExperiment);
                }

                return updatedExperiment;
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.saving = false;
            }
        },

        async deleteExperiment ({ id }: { id: string }) {
            this.saving = true;
            this.error = null;

            try {
                await ExperimentsService.deleteExperiment(id);
                this.removeExperiment(id);

                return true;
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.saving = false;
            }
        },

        async updateVariant ({ id, variant, guide, segment }: UpdateVariantPayload) {
            const experiment = this.getExperimentById(id);
            const newVariants = createVariantUpdate(
                guide.id,
                variant,
                experiment.variants as ExperimentVariant[],
            );
            const appId = validateVariantUpdate(
                experiment.state as ExperimentState,
                newVariants,
                useGuidesStore().map as unknown as Record<string, ExperimentGuide>,
            );
            let newAudience: Partial<AudienceUpdate> | null = {};
            if (appId) {
                newAudience = await this.createAudienceUpdate({ appId, experiment, segment });
            }

            await this.updateExperiment({
                id,
                experiment: {
                    ...experiment,
                    ...newAudience,
                    appId,
                    variants: newVariants,
                },
                options: {
                    variantUpdates: true,
                },
            });
        },

        async createAudienceUpdate ({
            appId,
            experiment,
            segment,
        }: CreateAudienceUpdatePayload): Promise<AudienceUpdate | null> {
            const app = useAppsStore().getAppById(String(appId));
            if (!app?.id) {
                throw new Error('Cannot update experiment audience without a valid app');
            }
            if (app.id === experiment.appId) return null;

            const segmentId = get(experiment, 'audienceUiHint.filters[0].segmentId', 'everyone');
            let newSegment = segment;

            if (!newSegment) {
                newSegment = getDefaultSegment(segmentId);
            }

            if (!newSegment) {
                // Lazy loading avoids the experiments/segments store initialization cycle.
                // eslint-disable-next-line @typescript-eslint/no-require-imports
                const { useSegmentsStore } = require('@/stores/segments.store');
                const segmentsStore = useSegmentsStore();
                await segmentsStore.loadOne({ id: segmentId });
                newSegment = segmentsStore.segmentById(segmentId);
            }

            const { audience, audienceUiHint } = await buildAudiencePipeline(newSegment, app);

            return { audience, audienceUiHint };
        },
    },
});

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise = null;
}

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useExperimentsStore, module.hot));
}
